import {
    Component,
    computed,
    inject,
    OnInit,
    ChangeDetectionStrategy,
    signal,
    Signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '@auth0/auth0-angular';
import { filter, map, take } from 'rxjs';
import {
    UIComponentSizeDirective,
    UILabelComponent,
    UILoaderComponent,
    UILogoComponent,
    UITypographyDirective
} from '@bannerflow/ui';

const ConnectionClaim = 'connection';
const DatabaseStrategy = 'auth0';
const TestPath = '/test-sso';

type TestOutcome = 'sso' | 'password' | 'no-email';

const Headings: Record<TestOutcome, string> = {
    sso: 'SSO Connection Works',
    password: 'Signed In With A Password, Not SSO',
    'no-email': 'No Email From SSO Connection'
};

const Messages: Record<TestOutcome, string> = {
    sso: 'The tested SSO connection works with the Bannerflow platform!',
    password:
        'This sign-in used a Bannerflow password, so the SSO connection has not been tested. It usually means the email domain is not routed to the connection.',
    'no-email': 'Sign-in succeeded, but the identity provider did not send an email address.'
};

function subjectSegments(subject: string | undefined): string[] {
    return subject?.split('|') ?? [];
}

function connectionFromSubject(subject: string | undefined): string {
    const parts: string[] = subjectSegments(subject);

    return parts.length > 2 ? parts[1] : (parts[0] ?? '');
}

function strategyFromSubject(subject: string | undefined): string {
    return subjectSegments(subject)[0] ?? '';
}

@Component({
    selector: 'app-sso-test',
    templateUrl: './sso.component.html',
    styleUrls: ['./sso.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        UIComponentSizeDirective,
        UILabelComponent,
        UILoaderComponent,
        UILogoComponent,
        UITypographyDirective
    ]
})
export class SsoComponent implements OnInit {
    private readonly auth: AuthService = inject(AuthService);

    private readonly claims = toSignal(this.auth.idTokenClaims$);
    private readonly loading = toSignal(this.auth.isLoading$, { initialValue: true });
    private readonly authenticated = toSignal(this.auth.isAuthenticated$, { initialValue: false });
    private readonly reauthenticated = toSignal(
        this.auth.appState$.pipe(map(state => state?.ssoTest === true)),
        { initialValue: false }
    );
    private readonly sdkError = toSignal(this.auth.error$.pipe(map(() => true)), {
        initialValue: false
    });
    private readonly didNotComplete = signal(false);

    public readonly failed: Signal<boolean> = this.didNotComplete.asReadonly();
    public readonly ready: Signal<boolean> = computed(
        () => !this.loading() && this.reauthenticated() && this.authenticated()
    );

    public readonly connection: Signal<string> = computed(() => {
        const claims = this.claims();

        return claims?.[ConnectionClaim] ?? connectionFromSubject(claims?.sub);
    });
    public readonly email: Signal<string> = computed(() => this.claims()?.email ?? '');
    public readonly hasEmail: Signal<boolean> = computed(() => !!this.email());

    public readonly outcome: Signal<TestOutcome> = computed(() => {
        if (strategyFromSubject(this.claims()?.sub) === DatabaseStrategy) {
            return 'password';
        }

        return this.hasEmail() ? 'sso' : 'no-email';
    });

    public readonly heading: Signal<string> = computed(() => Headings[this.outcome()]);
    public readonly message: Signal<string> = computed(() => Messages[this.outcome()]);

    ngOnInit(): void {
        const startedDuringCallback = this.loading();

        this.auth.isLoading$
            .pipe(
                filter(loading => !loading),
                take(1)
            )
            .subscribe(() => {
                if (this.reauthenticated()) {
                    return;
                }

                if (startedDuringCallback && this.sdkError()) {
                    this.didNotComplete.set(true);
                    return;
                }

                this.auth
                    .loginWithRedirect({
                        appState: { target: TestPath, ssoTest: true },
                        authorizationParams: {
                            prompt: 'login',
                            redirect_uri: `${window.location.origin}${TestPath}`
                        }
                    })
                    .subscribe({ error: () => this.didNotComplete.set(true) });
            });
    }
}
